/**
 * Widget placement mode — a sticker, block, icon or emoji attaches to the
 * cursor like every other tile's object (ROUND2 §3, ROUND3 §3): a plate arms
 * a `PlacementItem`, a ghost of the
 * preset at its own size follows the pointer from the last known position
 * (the `useDocPlacement` seeding, so there is no dead gap between the tray
 * and the board), a pane click places it where the ghost is, attached to a
 * section when it lands in one and selected, and the mode disarms —
 * single-shot, the same as the label and shape hooks. Escape cancels.
 *
 * Quick-place (`useCanvasWidgetQuickPlace`) stays for the flag-off gallery
 * and the context-menu Insert, which already has a position.
 */

import { useReactFlow } from "@xyflow/react";
import { useCallback, useEffect, useState } from "react";
import { CANVAS_SNAP, snapToGrid } from "../canvas-controls.config";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { attachNodeToSectionIfApplicable } from "../utils/sectionMembership";
import type { PlacementItem } from "./placementItems";

interface Position {
  x: number;
  y: number;
}

export interface UseWidgetPlacementParams {
  /** Last known cursor position over the canvas wrapper (screen coords). */
  lastCursorScreenPosRef: React.RefObject<Position | null>;
}

export function useWidgetPlacement({
  lastCursorScreenPosRef,
}: UseWidgetPlacementParams) {
  const { screenToFlowPosition, getNodes, getInternalNode } = useReactFlow();
  const { reactFlowWrapperRef } = useCanvasUi();
  const { addNode } = useCanvas();
  const [pendingPlacement, setPendingPlacement] =
    useState<PlacementItem | null>(null);
  const [ghostPosition, setGhostPosition] = useState<Position | null>(null);
  const isPlacingWidget = pendingPlacement !== null;

  // Grid-snapped node position centred under the given screen coords.
  const placementFromScreenPos = useCallback(
    (screenPos: Position, item: PlacementItem): Position => {
      const position = screenToFlowPosition(screenPos);
      const [gridX, gridY] = CANVAS_SNAP.grid;
      return {
        x: snapToGrid(position.x - item.width / 2, gridX),
        y: snapToGrid(position.y - item.height / 2, gridY),
      };
    },
    [screenToFlowPosition],
  );

  const startWidgetPlacement = useCallback(
    (item: PlacementItem, screenPos?: Position | null) => {
      setPendingPlacement(item);
      const cursor = screenPos ?? lastCursorScreenPosRef.current;
      if (cursor) lastCursorScreenPosRef.current = cursor;
      setGhostPosition(cursor ? placementFromScreenPos(cursor, item) : null);
    },
    [lastCursorScreenPosRef, placementFromScreenPos],
  );

  const cancelWidgetPlacement = useCallback(() => {
    setPendingPlacement(null);
    setGhostPosition(null);
  }, []);

  useEffect(() => {
    if (!isPlacingWidget) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelWidgetPlacement();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isPlacingWidget, cancelWidgetPlacement]);

  // Window-level tracking so the ghost follows from the first move — over
  // the toolbar, nodes and pane alike. Moves outside the wrapper are ignored.
  useEffect(() => {
    if (!pendingPlacement) return;
    const item = pendingPlacement;
    const handler = (e: MouseEvent) => {
      const wrapper = reactFlowWrapperRef.current;
      if (wrapper && !wrapper.contains(e.target as globalThis.Node)) return;
      const screenPos = { x: e.clientX, y: e.clientY };
      lastCursorScreenPosRef.current = screenPos;
      setGhostPosition(placementFromScreenPos(screenPos, item));
    };
    window.addEventListener("mousemove", handler);
    const cursor = lastCursorScreenPosRef.current;
    if (cursor) setGhostPosition(placementFromScreenPos(cursor, item));
    return () => window.removeEventListener("mousemove", handler);
  }, [
    pendingPlacement,
    reactFlowWrapperRef,
    lastCursorScreenPosRef,
    placementFromScreenPos,
  ]);

  const handlePaneClick = useCallback(
    (event: React.MouseEvent) => {
      if (!pendingPlacement) return;
      const position = placementFromScreenPos(
        { x: event.clientX, y: event.clientY },
        pendingPlacement,
      );
      const attached = attachNodeToSectionIfApplicable({
        node: pendingPlacement.build(position),
        nodes: getNodes(),
        getInternalNode,
      });
      addNode({ ...attached, selected: true } as Parameters<typeof addNode>[0]);
      setPendingPlacement(null);
      setGhostPosition(null);
    },
    [
      pendingPlacement,
      placementFromScreenPos,
      getNodes,
      getInternalNode,
      addNode,
    ],
  );

  return {
    isPlacingWidget,
    pendingPlacement,
    ghostPosition,
    startWidgetPlacement,
    cancelWidgetPlacement,
    handlePaneClick,
  };
}
